import type {
  ChatSelectedTemplateItem,
  SuggestTemplatesToolResult,
} from "schemas/generated/chatMessage";
import type { FileTemplate } from "schemas/generated/fileTemplate";

import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { useChatId } from "@v2/features/chat/hooks/useChatId";
import { useChatTurn } from "@v2/features/chat/hooks/useChatTurn";
import { useIsChatLoading } from "@v2/features/chat/store/ChatStoreContext";
import PageTemplateCard, {
  PageTemplateCardSkeleton,
} from "@v2/features/home/components/PageTemplateCard";
import { PageTemplatePreviewModal } from "@v2/features/home/components/PageTemplatePreviewModal";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { chatRouter, chatUtils } from "@v2/trpc/routes/chat";
import { fileTemplateRouter } from "@v2/trpc/routes/fileTemplate";
import {
  nextJSProjectRouter,
  nextJSProjectUtils,
} from "@v2/trpc/routes/nextJSProject";
import { PlusIcon } from "lucide-react";

function SelectedTemplate({ templateId }: { templateId: string }) {
  const projectId = useCurrentProjectId();
  const { data: template, isLoading } = fileTemplateRouter.get.useQuery(
    projectId
      ? {
          projectId,
          id: templateId,
        }
      : skipToken,
  );

  if (!template) {
    return null;
  }

  if (isLoading) {
    return <PageTemplateCardSkeleton />;
  }

  return <PageTemplateCard template={template} />;
}

function SuggestedTemplateCard({
  templateId,
  toolResult,
  toolResultMessageId,
}: {
  templateId: string;
  toolResult: SuggestTemplatesToolResult;
  toolResultMessageId: string;
}) {
  const logEvent = useLogAnalytics();
  const projectId = useCurrentProjectId();
  const [selectedTemplate, setSelectedTemplate] =
    React.useState<FileTemplate | null>(null);
  const chatId = useChatId();
  const isChatLoading = useIsChatLoading(chatId);
  const { data: template, isLoading } = fileTemplateRouter.get.useQuery(
    projectId
      ? {
          projectId,
          id: templateId,
        }
      : skipToken,
  );

  const { mutate: createPageFromTemplate, isPending: isCreatingPage } =
    nextJSProjectRouter.createPageFromTemplate.useMutation({
      onSuccess: (data) => {
        void handleUseCreatedFileFromTemplate(data.createdFile.id);
      },
    });

  const { mutate: updateMessage } = chatRouter.updateMessage.useMutation({
    onSuccess: () => {
      void chatUtils.get.invalidate({
        chatId: chatId!,
      });
      void chatUtils.getPaginatedMessages.invalidate({
        chatId: chatId!,
      });
    },
  });

  const { mutate: addMessage } = chatRouter.addMessage.useMutation({
    onSuccess: () => {
      if (chatId) {
        void chatUtils.get.invalidate({
          chatId,
        });
      }
    },
  });

  const { getChatTurnId } = useChatTurn();

  const handleUseCreatedFileFromTemplate = async (fileId: string) => {
    void nextJSProjectUtils.listPages.invalidate({ projectId });

    if (template && projectId && chatId && toolResult && toolResultMessageId) {
      updateMessage({
        id: toolResultMessageId,
        chatId,
        content: [
          {
            type: "tool-result",
            toolCallId: toolResult.toolCallId,
            toolName: toolResult.toolName,
            output: {
              type: "json",
              value: {
                ...toolResult?.output.value,
                selectedTemplateId: templateId,
              },
            },
          },
        ],
      });

      const selectedTemplateItem: ChatSelectedTemplateItem = {
        type: "selectedTemplate",
        selectedTemplateId: templateId,
        selectedTemplateName: template.name,
      };

      const chatTurnId = getChatTurnId();

      addMessage({
        chatId,
        content: [
          {
            type: "text",
            contentType: "selectedTemplate" as const,
            text: JSON.stringify(selectedTemplateItem),
            fileId,
          },
        ],
        role: "user",
        chatTurnId,
      });
    }
  };

  const handlePreviewClick = () => {
    if (!template) {
      return;
    }

    setSelectedTemplate(template);
  };

  const handleUseTemplateClick = (templateId: string) => {
    if (projectId) {
      logEvent("template.use", {
        origin: "chat",
        templateId,
      });
      createPageFromTemplate({
        projectId,
        templateId,
      });
    }
  };

  if (isLoading) {
    return <PageTemplateCardSkeleton />;
  }

  if (!template) {
    return null;
  }

  return (
    <>
      <div className="relative group w-full h-full">
        <PageTemplateCard template={template} />
        <div
          className="absolute inset-0 flex items-center justify-center rounded-xl transition-opacity duration-200 bg-white/40 opacity-0 group-hover:opacity-100"
          aria-hidden="true"
        >
          <div className="flex flex-col gap-3 items-center w-2/3">
            <Button
              variant="primary"
              size="base"
              onClick={() => handleUseTemplateClick(template.id)}
              startEnhancer={
                <PlusIcon className={twMerge(isChatLoading && "hidden")} />
              }
              className="w-full"
              loading={isCreatingPage || isChatLoading}
              disabled={isCreatingPage || isChatLoading}
            >
              Use Template
            </Button>
            <Button
              variant="secondary"
              size="base"
              onClick={handlePreviewClick}
              className="w-full"
              disabled={isCreatingPage}
            >
              Preview
            </Button>
          </div>
        </div>
      </div>
      {selectedTemplate && (
        <PageTemplatePreviewModal
          open
          onOpenChange={(open) => {
            if (!open) {
              setSelectedTemplate(null);
            }
          }}
          template={selectedTemplate}
          chatId={chatId ?? undefined}
          onSuccess={(fileId) => {
            void handleUseCreatedFileFromTemplate(fileId);
          }}
        />
      )}
    </>
  );
}

function SuggestedTemplates({
  toolResult,
  toolResultMessageId,
}: {
  toolResult: SuggestTemplatesToolResult | null;
  toolResultMessageId?: string;
}) {
  if (!toolResult || !toolResultMessageId) {
    return <div>Fetching templates...</div>;
  }

  if (!toolResult.output.value.suggestedTemplateIds) {
    return <div>No templates found</div>;
  }

  const selectedTemplateId = toolResult.output.value.selectedTemplateId;

  if (selectedTemplateId) {
    return (
      <div className="w-2/3">
        <SelectedTemplate templateId={selectedTemplateId} />
      </div>
    );
  }

  return (
    <div
      className="grid gap-4 w-full"
      style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}
    >
      {toolResult.output.value.suggestedTemplateIds.map((templateId) => (
        <SuggestedTemplateCard
          key={templateId}
          templateId={templateId}
          toolResult={toolResult}
          toolResultMessageId={toolResultMessageId}
        />
      ))}
    </div>
  );
}

export default SuggestedTemplates;
